export type PolishSpeechFilters = {
  examples: boolean;
  faq: boolean;
  warnings: boolean;
  footnotes: boolean;
};

export const DEFAULT_POLISH_SPEECH_FILTERS: PolishSpeechFilters = {
  examples: true,
  faq: true,
  warnings: true,
  footnotes: true,
};

export type PolishSpeechFragment = {
  id: string;
  text: string;
  blockId?: string;
};

export type PolishSpeechQueue = {
  total: number;
  getBatch: (startIndex: number, batchSize: number) => readonly PolishSpeechFragment[];
};

/** Keeps the native TTS queue bounded, even for very long rulebooks. */
export function createLazySpeechQueue(
  fragments: readonly PolishSpeechFragment[]
): PolishSpeechQueue {
  return {
    total: fragments.length,
    getBatch: (startIndex, batchSize) =>
      fragments.slice(Math.max(0, startIndex), Math.max(0, startIndex) + batchSize),
  };
}

export function getQueueBatch(
  queue: PolishSpeechQueue,
  position: number,
  batchSize = 12
): readonly PolishSpeechFragment[] {
  return queue.getBatch(position, batchSize);
}

export function findQueueIndex(
  queue: PolishSpeechQueue,
  fragmentIdPrefix: string,
  batchSize = 12
): number {
  for (let start = 0; start < queue.total; start += batchSize) {
    const index = queue.getBatch(start, batchSize)
      .findIndex((fragment) => fragment.id.startsWith(fragmentIdPrefix));
    if (index >= 0) return start + index;
  }
  return -1;
}

/** Resolves persisted identity against the current filtered queue, never its old index. */
export function resolveFragmentPosition(
  queue: PolishSpeechQueue,
  fragmentId: string
): number {
  const position = findQueueIndex(queue, fragmentId);
  return position >= 0 ? position : 0;
}

/** Maps a speech fragment to the visible content block that owns it. */
export function isActiveSpeechBlock(
  fragmentId: string | null | undefined,
  blockIndex: number
): boolean {
  return Boolean(fragmentId?.startsWith(`block-${blockIndex}-`));
}

export function shouldPromptNextChapter(
  state: 'idle' | 'speaking' | 'paused' | 'stopped',
  currentIndex: number,
  total: number,
  autoAdvance: boolean,
  hasNextChapter: boolean
): boolean {
  return state === 'idle' && total > 0 && currentIndex === total - 1 && autoAdvance && hasNextChapter;
}

export function shouldStopAfterFragment(
  currentIndex: number,
  stopAfterIndex: number | null
): boolean {
  return stopAfterIndex !== null && currentIndex === stopAfterIndex;
}
